import { useEffect, useMemo, useState } from 'react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { useRoles } from '../../hooks/use-roles';
import { api } from '../../lib/api';
import { lireContexteNav } from '../../lib/mini-app-contexte';
import { ATTENDUS_NATIONAUX_MCLM, TRES_BONNE_MAITRISE_MCLM, enDecrochage, estExtrapolee, pastilleDe, suggestionLsu } from './fluence-reperes';
import type { NiveauFluence } from './fluence-reperes';

interface Point {
  evaluationId: string; studentId: string; prenom: string; nom: string;
  completedAt: string; anneeScolaire: string | null;
  levelCode: string; supportTitre: string; mclm: number; dureeSecondes: number;
  seuilMin: number | null; seuilMax: number | null; referenceSeuil: string | null;
  position: 'sous' | 'dans' | 'au-dessus' | null;
  comprehensionReussies: number | null; comprehensionTotal: number | null;
}

interface Competence {
  code: string; libelle: string; notable: boolean;
  reussies: number; avecAide: number; nonReussies: number;
}


const L = 640;
const H = 200;
const M = { g: 40, d: 14, h: 16, b: 30 };

export default function FluenceEleveDetail() {
  const params = new URLSearchParams(window.location.search);
  const studentId = params.get('student') ?? '';
  /* Absents (ouverture directe depuis /mini-apps) : le bandeau reste le
     générique d'aujourd'hui — ce n'est un fil d'ariane que si un appelant
     (suivi de classe, vue d'école, devoirs) l'a fourni. */
  const { contexte, retourHref, retourLabel } = lireContexteNav(params);

  const [points, setPoints] = useState<Point[] | null>(null);
  const [anneesRetirees, setAnneesRetirees] = useState<string[]>([]);
  const [competences, setCompetences] = useState<Competence[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /* ZOOM = un facteur d'échelle horizontale sur le SVG, pas sur son viewBox : le
     conteneur défile déjà nativement (`overflow-x-auto`), donc élargir le SVG
     au-delà de la largeur visible espace les points et le défilement au doigt
     (déjà tactile par défaut) sert de pan. Pas de gestion manuelle du pan. */
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_PAS = 0.5;

  useEffect(() => {
    if (!studentId) { setErreur("Aucun élève n'a été désigné."); return; }
    void (api as never as { miniApps: { fluence: { progression: { eleve: { query: (i: unknown) => Promise<{ points: Point[]; anneesRetirees: string[] }> } } } } })
      .miniApps.fluence.progression.eleve.query({ studentId })
      .then((lu) => { setPoints(lu.points); setAnneesRetirees(lu.anneesRetirees); })
      .catch(() => setErreur("Cet historique n'a pas pu être lu."));

    /* LE PROFIL SE CHARGE À PART, et son échec ne casse pas la courbe. Les deux
       lectures répondent à deux questions différentes — « où en est-il » et
       « sur quoi bute-t-il » — et perdre la seconde ne doit pas coûter la
       première. */
    void (api as never as { miniApps: { fluence: { progression: { competences: { query: (i: unknown) => Promise<Competence[]> } } } } })
      .miniApps.fluence.progression.competences.query({ studentId })
      .then(setCompetences)
      .catch(() => setCompetences([]));
  }, [studentId]);

  /* L'API rend le plus récent d'abord ; une courbe se lit dans le sens du
     temps. */
  const suite = useMemo(() => [...(points ?? [])].reverse(), [points]);
  const eleve = points?.[0];
  /* DEUX lectures de suite sous le repère. C'est la page où un adulte vient
     comprendre : l'alerte doit s'y voir avant la courbe, pas après. */
  const decrochage = enDecrochage(points ?? []);
  /* SUGGESTION, jamais decision. Le LSU se saisit dans LSUN par l'enseignant. */
  const niveauClasse = params.get('niveauClasse') ?? undefined;
  const { activeRole } = useRoles();
  const suggestion = activeRole === 'teacher' ? suggestionLsu(points ?? [], niveauClasse) : { statut: 'masquee' as const };

  const courbe = useMemo(() => {
    if (suite.length === 0) return null;
    const valeurs = suite.map((p) => p.mclm);
    const bornes = suite.flatMap((p) => (p.seuilMin === null ? [] : [p.seuilMin, p.seuilMax ?? p.seuilMin]));
    /* L'échelle englobe les repères, sinon une bande dessinée hors cadre
       donnerait l'impression d'un élève très au-dessus ou très en dessous. */
    const bas = Math.max(0, Math.min(...valeurs, ...bornes) - 10);
    const haut = Math.max(...valeurs, ...bornes) + 10;
    const x = (i: number) => M.g + (suite.length === 1 ? (L - M.g - M.d) / 2 : (i * (L - M.g - M.d)) / (suite.length - 1));
    const y = (v: number) => M.h + (H - M.h - M.b) * (1 - (v - bas) / Math.max(1, haut - bas));
    return { bas, haut, x, y };
  }, [suite]);

  /* LES REPÈRES NATIONAUX, en pointillé — un par niveau traversé par la
     courbe, pas un par mesure : contrairement à la bande grise (le repère au
     jour de la mesure, qui change avec chaque lecture), ce sont des valeurs
     fixes qui ne dépendent pas de la date. Un élève qui passe du CE1 au CE2
     voit donc deux paires de lignes, une par niveau — jamais plus d'une paire
     par niveau, même si plusieurs mesures partagent le même niveau.
     DEUX lignes, pas une « début/fin d'année » : demande de Dogan (30/09),
     recoupée avant affichage — aucune source Éduscol primaire ne donne de
     granularité mensuelle. `attendu` est le niveau attendu par classe ; `tresBonneMaitrise`
     est la frontière de la très bonne maîtrise (1,15 × l'attendu) — voir
     `TRES_BONNE_MAITRISE_MCLM`. */
  const reperesNiveaux = useMemo(() => {
    if (!courbe) return [];
    const niveaux = [...new Set(suite.map((p) => p.levelCode))];
    return niveaux
      .map((niveau) => ({
        niveau,
        attendu: ATTENDUS_NATIONAUX_MCLM[niveau as NiveauFluence],
        tresBonneMaitrise: TRES_BONNE_MAITRISE_MCLM[niveau as NiveauFluence],
      }))
      .filter((r): r is { niveau: string; attendu: number; tresBonneMaitrise: number } =>
        r.attendu !== undefined && r.tresBonneMaitrise !== undefined)
      .sort((a, b) => a.attendu - b.attendu);
  }, [suite, courbe]);

  const attendus = useMemo(() => reperesNiveaux
    .map((r) => ({ niveau: r.niveau, valeur: r.attendu }))
    .filter((a) => courbe && a.valeur >= courbe.bas && a.valeur <= courbe.haut),
  [reperesNiveaux, courbe]);

  const tresBonneMaitrise = useMemo(() => reperesNiveaux
    .map((r) => ({ niveau: r.niveau, valeur: r.tresBonneMaitrise }))
    .filter((a) => courbe && a.valeur >= courbe.bas && a.valeur <= courbe.haut),
  [reperesNiveaux, courbe]);

  const carte = 'bg-card border border-border rounded-card shadow-card p-5';

  return (
    <>
      <HeaderContent width="lg" showBurger backTo={retourHref} backLabel={retourLabel}>
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>
            {contexte ? `${contexte} › ` : ''}{eleve ? `${eleve.prenom} ${eleve.nom}` : 'Élève'}
          </h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Évolution de la lecture à voix haute, mesure après mesure.
          </p>
        </div>
      </HeaderContent>

      <main className="max-w-[1000px] mx-auto px-5 pb-16 pt-4 flex flex-col gap-5">
        {erreur ? (
          <p className="m-0 rounded-sm bg-naia-orange-bg px-4 py-3 text-sm text-foreground">{erreur}</p>
        ) : null}

        {anneesRetirees.length > 0 ? (
          <p className="m-0 rounded-sm border border-border bg-naia-info-bg px-4 py-3 text-sm text-foreground">
            <b>
              {anneesRetirees.length === 1
                ? `L’année ${anneesRetirees[0]} n’est pas affichée.`
                : `Les années ${anneesRetirees.join(', ')} ne sont pas affichées.`}
            </b>{' '}
            Les données d’un élève ne restent accessibles à l’enseignant que pendant
            l’année scolaire où elles ont été recueillies. La direction de
            l’établissement y a toujours accès.
          </p>
        ) : null}

        {decrochage ? (
          <p className="m-0 rounded-sm border border-naia-orange bg-naia-orange-bg px-4 py-3 text-sm text-foreground">
            <b>Les deux dernières lectures sont sous le repère du niveau.</b> Une lecture faible
            arrive à tout le monde ; deux d&apos;affilée sont une tendance, et c&apos;est le moment
            de regarder de près.
          </p>
        ) : null}

        {points === null ? null : suite.length === 0 ? (
          <section className={carte}>
            <p className="m-0 text-[16px] font-bold text-foreground mb-1">Aucune lecture mesurée.</p>
            <p className="m-0 text-sm text-muted-foreground">
              La courbe apparaîtra dès la première lecture enregistrée.
            </p>
          </section>
        ) : (
          <>
            <section className={carte}>
              <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Évolution</h2>
              <p className="m-0 mb-4 text-[13px] text-muted-foreground">
                Chaque point est une lecture. La bande grise est le repère du niveau <b>au jour de
                la mesure</b> — pas celui d&apos;aujourd&apos;hui : un repère qui change ne doit pas
                réécrire le passé d&apos;un élève. La ligne <b>bleue en pointillé</b> est le niveau
                attendu par classe ; la ligne <b>verte en pointillé</b> est la frontière de très
                bonne maîtrise — toutes deux fixes, une paire par niveau, quelle que soit la date
                (pas une progression mensuelle : aucune source ne la donne). Un point <b>creux et
                orangé</b> signale une lecture de moins d&apos;une minute, dont le nombre de mots par
                minute est extrapolé.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_PAS))}
                  disabled={zoom <= ZOOM_MIN}
                  className="w-11 h-11 shrink-0 rounded-sm border border-border bg-card text-foreground text-lg font-bold disabled:opacity-40"
                  aria-label="Dézoomer le graphique">−</button>
                <button type="button" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_PAS))}
                  disabled={zoom >= ZOOM_MAX}
                  className="w-11 h-11 shrink-0 rounded-sm border border-border bg-card text-foreground text-lg font-bold disabled:opacity-40"
                  aria-label="Zoomer le graphique">+</button>
                {zoom > ZOOM_MIN ? (
                  <button type="button" onClick={() => setZoom(ZOOM_MIN)}
                    className="h-11 shrink-0 rounded-sm border border-border bg-card px-3 text-sm text-foreground"
                  >Réinitialiser</button>
                ) : null}
                <span className="text-[12px] text-muted-foreground">
                  {zoom > ZOOM_MIN ? 'Fais glisser le graphique pour voir les mesures cachées.' : ''}
                </span>
              </div>
              <div className="overflow-x-auto touch-pan-x">
                <svg viewBox={`0 0 ${L} ${H}`} style={{ width: `${zoom * 100}%`, minWidth: 420 }} className="h-auto" role="img"
                  aria-label={`Évolution du nombre de mots correctement lus par minute, ${suite.length} mesure(s)`}>
                  {courbe ? (
                    <>
                      {/* La bande du repère, segment par segment : elle peut
                          changer d'une mesure à l'autre, et au CP elle n'existe pas. */}
                      {suite.map((p, i) => {
                        if (p.seuilMin === null || p.seuilMax === null || i === suite.length - 1) return null;
                        const s = suite[i + 1]!;
                        if (s.seuilMin === null || s.seuilMax === null) return null;
                        return (
                          <polygon
                            key={`b${p.evaluationId}`}
                            points={`${courbe.x(i)},${courbe.y(p.seuilMin)} ${courbe.x(i + 1)},${courbe.y(s.seuilMin)} ${courbe.x(i + 1)},${courbe.y(s.seuilMax)} ${courbe.x(i)},${courbe.y(p.seuilMax)}`}
                            className="fill-muted"
                            opacity="0.55"
                          />
                        );
                      })}
                      {suite.length === 1 && suite[0]!.seuilMin !== null ? (
                        <rect
                          x={M.g} width={L - M.g - M.d}
                          y={courbe.y(suite[0]!.seuilMax ?? 0)}
                          height={Math.max(1, courbe.y(suite[0]!.seuilMin) - courbe.y(suite[0]!.seuilMax ?? 0))}
                          className="fill-muted" opacity="0.55"
                        />
                      ) : null}

                      {[courbe.bas, Math.round((courbe.bas + courbe.haut) / 2), courbe.haut].map((v) => (
                        <g key={`g${v}`}>
                          <line x1={M.g} x2={L - M.d} y1={courbe.y(v)} y2={courbe.y(v)} className="stroke-border" strokeWidth="1" />
                          <text x={M.g - 8} y={courbe.y(v) + 4} textAnchor="end" className="fill-muted-foreground" fontSize="11">{Math.round(v)}</text>
                        </g>
                      ))}

                      {attendus.map((a) => (
                        <g key={`att${a.niveau}`}>
                          <line
                            x1={M.g} x2={L - M.d} y1={courbe.y(a.valeur)} y2={courbe.y(a.valeur)}
                            className="stroke-naia-blue" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.7"
                          />
                          <text x={L - M.d} y={courbe.y(a.valeur) - 4} textAnchor="end" className="fill-naia-blue" fontSize="10.5" fontWeight="700">
                            {a.niveau} attendu · {a.valeur}
                          </text>
                        </g>
                      ))}

                      {tresBonneMaitrise.map((a) => (
                        <g key={`tbm${a.niveau}`}>
                          <line
                            x1={M.g} x2={L - M.d} y1={courbe.y(a.valeur)} y2={courbe.y(a.valeur)}
                            className="stroke-naia-green-deep" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.7"
                          />
                          <text x={L - M.d} y={courbe.y(a.valeur) - 4} textAnchor="end" className="fill-naia-green-deep" fontSize="10.5" fontWeight="700">
                            {a.niveau} très bonne maîtrise · {a.valeur}
                          </text>
                        </g>
                      ))}

                      <polyline
                        points={suite.map((p, i) => `${courbe.x(i)},${courbe.y(p.mclm)}`).join(' ')}
                        fill="none" className="stroke-naia-blue" strokeWidth="2.5"
                        strokeLinejoin="round" strokeLinecap="round"
                      />
                      {suite.map((p, i) => (
                        <g key={p.evaluationId}>
                          {/* Creux et cerclé quand la mesure est extrapolée :
                              la courbe doit distinguer d'un coup d'œil ce qui a
                              été mesuré de ce qui a été projeté. */}
                          <circle
                            cx={courbe.x(i)} cy={courbe.y(p.mclm)} r="5"
                            className={estExtrapolee(p.dureeSecondes) ? 'fill-card stroke-naia-orange' : 'fill-naia-blue'}
                            strokeWidth={estExtrapolee(p.dureeSecondes) ? 2.5 : 0}
                          />
                          <text x={courbe.x(i)} y={courbe.y(p.mclm) - 11} textAnchor="middle" className="fill-foreground" fontSize="12" fontWeight="800">
                            {p.mclm}
                          </text>
                          <text x={courbe.x(i)} y={H - 8} textAnchor="middle" className="fill-muted-foreground" fontSize="11">
                            {p.completedAt ? new Date(p.completedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}
                          </text>
                        </g>
                      ))}
                    </>
                  ) : null}
                </svg>
              </div>
              {/* Un progrès de quelques mots par minute n'est pas du bruit à cet
                  âge, mais deux mesures ne font pas une tendance : le dire évite
                  de tirer une conclusion d'un point. */}
              {/* La référence est figée AVEC chaque résultat, précisément parce
                  qu'elle dit que l'arrêté n'a pas été retrouvé. La geler sans
                  jamais la montrer revenait à présenter un repère indicatif
                  comme un repère officiel. */}
              {suite.find((p) => p.referenceSeuil)?.referenceSeuil ? (
                <p className="m-0 mt-3 text-[12.5px] text-muted-foreground leading-relaxed">
                  <b>D&apos;où vient le repère :</b> {suite.find((p) => p.referenceSeuil)!.referenceSeuil}
                  {' '}Les lignes en pointillé reprennent la même source — l&apos;arrêté exact reste à
                  confirmer.
                </p>
              ) : null}
              {suite.length < 3 ? (
                <p className="m-0 mt-3 text-[13px] text-muted-foreground">
                  {suite.length === 1 ? 'Une seule mesure : la courbe se lira à partir de la deuxième.' : 'Deux mesures ne font pas encore une tendance.'}
                </p>
              ) : null}
            </section>

            <section className={carte}>
              <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Chaque lecture</h2>
              {/* Le score de compréhension ne couvre QUE les questions à choix.
                  Mesuré sur le corpus : elles représentent 35 % à 67 % des
                  questions d'un texte, 46 % en médiane. « 4/6 » sur un texte qui
                  en pose treize se lisait donc comme « on lui en a posé six » —
                  le chiffre était vrai, sa portée tue, et la part tue est la
                  majorité. Les questions ouvertes ne sont pas notées : elles se
                  relisent avec un adulte. */}
              <p className="m-0 mb-3 text-[13px] text-muted-foreground">
                Le score de compréhension ne porte que sur les questions à choix — environ la
                moitié de chaque texte. Les questions ouvertes ne sont pas notées : elles se
                relisent avec l&apos;élève.
              </p>
              <ul className="list-none m-0 p-0 flex flex-col gap-2">
                {(points ?? []).map((p) => {
                  const pastille = pastilleDe(p.position);
                  return (
                    <li key={p.evaluationId} className="flex items-center gap-3 border-b border-border pb-2 last:border-none">
                      <span className="text-[13px] text-muted-foreground w-[92px] shrink-0">
                        {p.completedAt ? new Date(p.completedAt).toLocaleDateString('fr-FR') : '—'}
                      </span>
                      <span className="text-sm text-foreground min-w-0 truncate">{p.supportTitre}</span>
                      <span className="ml-auto text-sm font-extrabold text-foreground shrink-0">{p.mclm} MCLM</span>
                      {/* Un chiffre extrapolé qui ne le dit pas se lit comme une
                          mesure. Sous une minute il l'est, et d'autant plus
                          fragile que la lecture est courte. */}
                      {estExtrapolee(p.dureeSecondes) ? (
                        <span className="shrink-0 text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-naia-orange-bg text-naia-orange">
                          extrapolé · {p.dureeSecondes} s
                        </span>
                      ) : null}
                      <span className={`shrink-0 text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full ${pastille.cls}`}>
                        {pastille.label}
                      </span>
                      {p.comprehensionTotal ? (
                        <span className="text-[13px] text-muted-foreground shrink-0">
                          compréhension {p.comprehensionReussies}/{p.comprehensionTotal}
                        </span>
                      ) : (
                        <span className="text-[13px] text-muted-foreground shrink-0">lecture seule</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* LE POSITIONNEMENT LSU — vérifié sur Éduscol « Collège : les
                bilans » (février 2026) : le livret scolaire unique va du CP à la
                troisième, et le bilan de fin de cycle emploie ces quatre niveaux
                exacts. La fluence n'alimente qu'UNE composante du domaine 1 du
                socle. Elle ne positionne ni le domaine, ni le socle — et le dire
                est ce qui sépare un outil d'aide d'un outil qui décide. */}
            {suggestion.statut === 'affichee' ? (
              <section className={carte}>
                <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Positionnement LSU — suggestion</h2>
                <p className="m-0 mb-3 text-[13px] text-muted-foreground">
                  Sur la composante <b>« comprendre et s&apos;exprimer en utilisant la langue
                  française à l&apos;oral »</b>, domaine 1 du socle. Calculé depuis la dernière
                  lecture situable.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-sm bg-naia-info-bg px-3 py-2 text-[15px] font-extrabold text-naia-blue">
                    {suggestion.positionnement.niveau} — {suggestion.positionnement.libelle}
                  </span>
                </div>
                <p className="m-0 mt-3 text-[13px] text-muted-foreground leading-relaxed">
                  <b>La décision vous appartient.</b> Ce niveau est une suggestion calculée sur une
                  seule composante : le bilan de fin de cycle en couvre huit, et tient compte de bien
                  d&apos;autres observations. Validez, ajustez, puis recopiez dans LSUN —
                  <b> aucun export automatique n&apos;est fait depuis cet écran</b>.
                </p>
              </section>
            ) : suggestion.statut === 'texte-different' ? (
              <section className={carte}>
                <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Positionnement LSU — suggestion</h2>
                <p className="m-0 text-[13px] text-muted-foreground">
                  Suggestion LSU indisponible : le texte lu n&apos;est pas du niveau de la classe.
                </p>
              </section>
            ) : null}

            {/* CE QU'IL RÉUSSIT, PAS SEULEMENT COMBIEN. Un « 4 sur 7 » dit qu'il
                a échoué trois fois ; il ne dit pas qu'il prélève bien et qu'il
                n'infère pas. C'est pourtant la seule information sur laquelle un
                enseignant peut agir.
                Les 1 279 questions ouvertes du corpus portaient déjà ces codes —
                écrits par l'ingestion, et lus par personne jusqu'ici. */}
            {competences && competences.some((c) => c.reussies + c.avecAide + c.nonReussies > 0) ? (
              <section className={carte}>
                <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Par compétence de lecture</h2>
                <p className="m-0 mb-4 text-[13px] text-muted-foreground">
                  Ce que les questions de compréhension ont réellement mis à l&apos;épreuve. Une
                  compétence sans aucune réponse n&apos;a pas encore été rencontrée — ce n&apos;est
                  pas un échec.
                </p>
                <ul className="list-none m-0 p-0 flex flex-col gap-2">
                  {competences.map((c) => {
                    const total = c.reussies + c.avecAide + c.nonReussies;
                    return (
                      <li key={c.code} className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-card px-3 py-2">
                        <span className="text-[11px] font-extrabold uppercase tracking-[0.05em] text-muted-foreground shrink-0">{c.code}</span>
                        <span className="min-w-0 flex-1 text-sm text-foreground">{c.libelle}</span>
                        {total === 0 ? (
                          <span className="shrink-0 text-[13px] text-muted-foreground">pas encore rencontrée</span>
                        ) : (
                          <span className="shrink-0 flex flex-wrap items-center gap-1.5">
                            {!c.notable ? <span className="text-[13px] text-muted-foreground">non comptée dans les taux</span> : null}
                            {c.reussies > 0 ? <span className="rounded-sm bg-naia-green-bg px-2 py-[3px] text-[12px] font-extrabold text-naia-green-deep">{c.reussies} réussie{c.reussies > 1 ? 's' : ''}</span> : null}
                            {c.avecAide > 0 ? <span className="rounded-sm bg-naia-info-bg px-2 py-[3px] text-[12px] font-extrabold text-naia-blue">{c.avecAide} avec aide</span> : null}
                            {c.nonReussies > 0 ? <span className="rounded-sm bg-naia-orange-bg px-2 py-[3px] text-[12px] font-extrabold text-naia-orange">{c.nonReussies} non réussie{c.nonReussies > 1 ? 's' : ''}</span> : null}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {/* Les libellés sont DÉDUITS des énoncés du corpus : ni le corpus
                    ni le prototype ne les nomment. Le code brut reste affiché pour
                    qu'un enseignant puisse en juger lui-même. */}
                <p className="m-0 mt-3 text-[13px] text-muted-foreground">
                  Les intitulés sont déduits des questions du corpus, qui ne les nomme pas. Le code
                  d&apos;origine est conservé à gauche.
                </p>
              </section>
            ) : null}
          </>
        )}
      </main>
    </>
  );
}
